import type {
	ApplicationError,
	ForManagingProjects,
	News,
	Project,
	ProjectDetails,
	ProjectV2
} from 'core'
import { z } from 'zod'

import { HTTPClient, HTTPMethods } from './http_client'
import type {
	CollaboratorDTO,
	DAppDTOV2,
	ProjectDetailDTO,
	ProjectDTO,
	ProjectDTOV2
} from './types'

export type AddCollaboratorPayload = {
	user_handler: string
	role: string
}

export class ProjectsManager implements ForManagingProjects {
	readonly client = new HTTPClient()

	async getProjectsAndNews() {
		const response = await this.client.fetch<{
			news: News[]
			projects: ProjectDTOV2[]
		}>(`/authv2/home/api/v1/home?news=10`)

		if (response.error) {
			return { error: response.error }
		}

		return {
			data: {
				news: response.data?.news || [],
				projects: response.data!.projects.map(this.formatProjectV2) || []
			}
		}
	}

	async getUserProjects() {
		const response = await this.client.fetch<{
			news: News[]
			projects: ProjectDTO[] | null
		}>(
			// TODO: Get query params from function arguments
			`/authv2/home/api/v1/home?news=10&projects_page=0&projects_page_size=10`
		)

		if (response.error) {
			return { error: response.error }
		}

		return {
			data: {
				news: response.data?.news || [],
				projects: response.data?.projects?.map(this.formatProject) || []
			}
		}
	}

	// TODO: Find a better name for this function
	async getUserProjectDetails(projectId: string) {
		const response = await this.client.fetch<ProjectDetailDTO>(
			`/authv2/home/api/v1/projects/${projectId}`
		)

		if (response.error) {
			return { error: response.error }
		}

		return {
			data: this.formatProjectDetails(response.data!)
		}
	}

	async createProject(
		payload: z.infer<typeof ForManagingProjects.CreateProjectPayload>
	) {
		const response = await this.client.fetch<
			ProjectDTO,
			{
				description: string
				name: string
				project_id: string
			}
		>(`/authv2/projects/api/v1/projects`, {
			method: HTTPMethods.Post,
			data: {
				description: payload.description,
				name: payload.name,
				project_id: payload.projectId
			}
		})

		if (response.error) {
			return {
				error: response.error
			}
		}

		return {
			data: this.formatProject(response.data!)
		}
	}

	// TODO: Create the test cases for this function
	async createSmartContract(
		payload: z.infer<typeof ForManagingProjects.CreateSmartContract>
	) {
		const response = await this.client.fetch<
			DAppDTOV2,
			{
				smart_contract: {
					name: string
				}
				project: {
					name: string
					project_id?: string
				}
			}
		>(`/authv2/projects/api/v1/smart_contracts`, {
			method: HTTPMethods.Post,
			data: {
				smart_contract: {
					name: payload.smartContract.name
				},
				project: {
					name: payload.project.name,
					project_id: payload.project.projectId
				}
			}
		})

		if (response.error) {
			return {
				error: response.error
			}
		}

		return {
			data: this.formatDApp(response.data!)
		}
	}

	// TODO: Have to create the test cases for this function
	async addCollaborator(
		payload: z.infer<typeof ForManagingProjects.AddCollaboratorPayload>
	) {
		const response = await this.client.fetch<
			CollaboratorDTO,
			AddCollaboratorPayload
		>(
			`/authv2/projects/api/v1/projects/${payload.publicProjectId}/collaborators`,
			{
				method: HTTPMethods.Post,
				data: {
					user_handler: payload.userHandler,
					role: payload.role
				}
			}
		)

		if (response.error) {
			return { error: response.error }
		}

		return {
			data: {
				userHandler: response.data!.user_handler,
				role: response.data!.role,
				// Return "pending" status because is a new invitation
				// This means that the user have to accept or reject the invitation
				status: 'pending'
			}
		}
	}

	// TODO: Have to create the test cases for this function
	async removeCollaborator(
		payload: z.infer<typeof ForManagingProjects.RemoveCollaboratorPayload>
	) {
		const encodedEmail = encodeURIComponent(payload.collaboratorUsername)

		const response = await this.client.fetch<{
			code: string
		}>(
			`/authv2/projects/api/v1/projects/${payload.publicProjectId}/collaborators/${encodedEmail}`,
			{
				method: HTTPMethods.Delete
			}
		)

		return { error: response.error }
	}

	// TODO: Have to create the test cases for this function
	async acceptCollaboratorInvitation(
		payload: z.infer<typeof ForManagingProjects.AcceptCollaboratorInvitation>
	) {
		const { error, data } = await this.client.fetch<ProjectDTOV2>(
			`/authv2/projects/api/v1/projects/${payload.publicProjectId}/collaborators/accept`,
			{
				method: HTTPMethods.Post
			}
		)

		if (error) return { error }

		return {
			data: this.formatProjectV2(data!)
		}
	}

	// TOOD: First implementation have to review the format of the response
	private formatDApp(dApp: DAppDTOV2): ProjectV2 {
		return {
			active: dApp.active,
			createdAt: dApp.created_at,
			description: dApp.description,
			id: dApp.id,
			name: dApp.name,
			publicId: dApp.public_id,
			isCollaborator: dApp.is_collaborator,
			owner: {
				id: dApp.owner.id,
				email: dApp.owner.email,
				firstName: dApp.owner.first_name,
				lastName: dApp.owner.last_name
			},
			smartContracts: dApp.smart_contracts?.map(smartContract => ({
				active: smartContract.active,
				createdAt: smartContract.created_at,
				description: smartContract.description,
				id: smartContract.id,
				name: smartContract.name,
				projectId: smartContract.project_id,
				status: smartContract.status,
				version: smartContract.version,
				webIdeUrl: smartContract.web_ide_url
			}))
		}
	}

	private formatProjectV2(project: ProjectDTOV2): ProjectV2 {
		return {
			active: project.active,
			createdAt: project.created_at,
			description: project.description,
			id: project.id,
			name: project.name,
			publicId: project.public_id,
			isCollaborator: project.is_collaborator,
			owner: {
				id: project.owner.id,
				email: project.owner.email,
				firstName: project.owner.first_name,
				lastName: project.owner.last_name
			},
			smartContracts: project.smart_contracts.map(smartContract => ({
				active: smartContract.active,
				createdAt: smartContract.created_at,
				description: smartContract.description,
				id: smartContract.id,
				name: smartContract.name,
				projectId: project.public_id,
				status: smartContract.status,
				version: smartContract.version,
				webIdeUrl: smartContract.web_ide_url
			}))
		}
	}

	private formatProject(project: ProjectDTO): Project {
		return {
			active: project.active,
			createdAt: project.created_at,
			description: project.description,
			id: project.id,
			name: project.name,
			publicId: project.public_id,
			isCollaborator: project.is_collaborator,
			...(project.owner && {
				owner: {
					id: project.owner.id,
					email: project.owner.email,
					firstName: project.owner.first_name,
					lastName: project.owner.last_name
				}
			})
		}
	}

	private formatProjectDetails(project: ProjectDetailDTO): ProjectDetails {
		return {
			active: project.active,
			createdAt: project.created_at,
			description: project.description,
			name: project.name,
			publicId: project.public_id,
			isCollaborator: project.is_collaborator,
			dapps:
				project.dapps?.map(dapp => ({
					active: dapp.active,
					createdAt: dapp.created_at,
					id: dapp.id,
					name: dapp.name,
					webIdeUrl: dapp.web_ide_url,
					version: dapp.version,
					description: dapp.description
				})) || [],
			collaborators:
				project.collaborators?.map(collaborator => ({
					role: collaborator.role,
					status: collaborator.status || '',
					userHandler: collaborator.user_handler
				})) || [],
			...(project.owner && {
				owner: {
					id: project.owner.id,
					email: project.owner.email,
					firstName: project.owner.first_name,
					lastName: project.owner.last_name
				}
			})
		}
	}
}

export class ProjectsManagerMock implements ForManagingProjects {
	getProjectsAndNews() {
		return Promise.resolve({})
	}

	getUserProjects() {
		return Promise.resolve({
			data: {
				news: [
					{
						content: 'string',
						description: 'New test description',
						title: 'New crypto-currency'
					}
				],
				projects: [
					{
						active: true,
						createdAt: '2022-01-01T00:00:00+00:00',
						description: 'Project Description',
						id: '018f1450-0e7c-7b2d-aeb2-a8ec7964abae',
						name: 'Project Name',
						publicId: 'project-c931d4c3922c',
						isCollaborator: true,
						owner: {
							id: '0190e5c7-2683-784f-9555f88afb32',
							email: 'ezequiel.martinez@codigo.ai',
							firstName: 'Ezequiel',
							lastName: 'Martinez'
						}
					}
				]
			}
		})
	}

	getUserProjectDetails(id: string) {
		return Promise.resolve({
			data: {
				createdAt: '2022-01-01T00:00:00+00:00',
				description: 'Project Description',
				name: 'Project Name',
				publicId: id,
				isCollaborator: true,
				dapps: [
					{
						active: true,
						createdAt: '2022-01-01T00:00:00+00:00',
						id: '018f1450-0e7c-7b2d-aeb2-a8ec7964abae',
						name: 'Dapp Name',
						webIdeUrl: '',
						version: '1.0.0',
						description: 'My DApp description'
					}
				],
				collaborators: [
					{
						userHandler: 'ezequiel.martinez',
						role: 'owner',
						status: 'pending'
					}
				],
				owner: {
					id: '0190e5c7-2683-784f-9555f88afb32',
					email: 'fakeemail@mail.com',
					firstName: 'Ezequiel',
					lastName: 'Martinez'
				}
			}
		})
	}

	async createProject(
		payload: z.infer<typeof ForManagingProjects.CreateProjectPayload>
	) {
		return Promise.resolve({
			data: {
				active: true,
				createdAt: '2024-05-23T20:31:18.935931Z',
				description: payload.description,
				id: '018fa726-f222-72b5-9081-a44c17e2f901',
				name: payload.name,
				publicId: 'project-a44c17e2f901',
				isCollaborator: false,
				owner: {
					id: '0190e5c7-2683-784f-9555f88afb32',
					email: 'fakeemail@mail.com',
					firstName: 'Ezequiel',
					lastName: 'Martinez'
				}
			}
		})
	}

	async createSmartContract(
		payload: z.infer<typeof ForManagingProjects.CreateSmartContract>
	) {
		return Promise.resolve({
			data: {
				active: true,
				createdAt: '2024-11-07T12:56:10.10339Z',
				description: '',
				id: '018fa726-f222-72b5-9081-a44c17e2f901',
				name: payload.project.name,
				publicId: payload.project.projectId ?? 'public-id-genereated',
				isCollaborator: false,
				owner: {
					id: '0190e5c7-2683-784f-9555f88afb32',
					email: 'fakeemail@mail.com',
					firstName: 'Ezequiel',
					lastName: 'Martinez'
				},
				smartContracts: [
					{
						active: true,
						createdAt: '2024-11-07T12:56:10.116039Z',
						description: '',
						id: '019306b2-5283-7461-b947-1bd525970938',
						name: payload.smartContract.name,
						projectId: '',
						status: 'READY',
						version: '0.0.0',
						webIdeUrl:
							'/ide/env-f5b619febe03d932ab71197e1d07eb5031798df99594148b418991edfe'
					}
				]
			}
		})
	}

	async addCollaborator(
		payload: z.infer<typeof ForManagingProjects.AddCollaboratorPayload>
	) {
		return Promise.resolve({
			data: {
				userHandler: payload.userHandler,
				role: payload.role,
				status: 'pending'
			}
		})
	}

	async removeCollaborator(): Promise<{ error?: ApplicationError }> {
		throw new Error('Method not implemented.')
	}

	async acceptCollaboratorInvitation(): Promise<{ error?: ApplicationError }> {
		throw new Error('Method not implemented.')
	}
}
